import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { FarmProvider } from './contexts/FarmContext';

// Components
import Navbar from './components/layout/Navbar';
import Sidebar from './components/layout/Sidebar';
import LoadingSpinner from './components/common/LoadingSpinner';
import FeedbackButton from './components/common/FeedbackButton';
import SubscriptionBanner from './components/common/SubscriptionBanner';
import ErrorBoundary from './components/common/ErrorBoundary';

// Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import OAuthCallback from './pages/auth/OAuthCallback';
import AcceptInvite from './pages/auth/AcceptInvite';
import Dashboard from './pages/Dashboard';
import FarmSetup from './pages/farm/FarmSetup';
import FarmManagement from './pages/farm/FarmManagement';
import FlockList from './pages/farm/FlockList';
import Farms from './pages/farm/Farms';
import FarmSettings from './pages/farm/FarmSettings';
import Activities from './pages/activities/Activities';
import Records from './pages/records/Records';
import AIAssistant from './pages/ai/AIAssistant';
import Hatching from './pages/hatching/Hatching';
import BatchDetail from './pages/hatching/BatchDetail';
import Profile from './pages/Profile';
import Market from './pages/Market';
import Admin from './pages/Admin';
import Landing from './pages/Landing';
import Billing from './pages/Billing';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Shop from './pages/shop/Shop';
import PublicShop from './pages/shop/PublicShop';
import SharedChat from './pages/ai/SharedChat';
import Academy from './pages/academy/Academy';
import AcademyCourse from './pages/academy/AcademyCourse';

// Supplier Pages
import SupplierDashboard from './pages/supplier/SupplierDashboard';
import SupplierProducts from './pages/supplier/SupplierProducts';
import SupplierOrders from './pages/supplier/SupplierOrders';
import SupplierSettings from './pages/supplier/SupplierSettings';

// Client Pages
import ClientDashboard from './pages/client/ClientDashboard';
import ClientExplore from './pages/client/ClientExplore';
import ClientOrders from './pages/client/ClientOrders';
import ClientSettings from './pages/client/ClientSettings';

// Shared Pages
import Wallet from './pages/Wallet';

// Dashboard route wrapper - redirects suppliers/clients to their dashboards
const DashboardRedirect = () => {
  const { user } = useAuth();
  const accountType = user?.accountType || 'farmer';
  
  if (accountType === 'supplier') {
    return <Navigate to="/supplier/dashboard" replace />;
  }
  if (accountType === 'client') {
    return <Navigate to="/client/dashboard" replace />;
  }
  return <Dashboard />;
};

// Farmer-only route wrapper - redirects suppliers/clients to their dashboards
const FarmerOnlyRoute = ({ children }) => {
  const { user } = useAuth();
  const accountType = user?.accountType || 'farmer';
  
  if (accountType === 'supplier') {
    return <Navigate to="/supplier/dashboard" replace />;
  }
  if (accountType === 'client') {
    return <Navigate to="/client/dashboard" replace />;
  }
  return children;
};

// Profile route wrapper - redirects suppliers/clients to their settings pages
const ProfileRedirect = () => {
  const { user } = useAuth();
  const accountType = user?.accountType || 'farmer';
  
  if (accountType === 'supplier') {
    return <Navigate to="/supplier/settings" replace />;
  }
  if (accountType === 'client') {
    return <Navigate to="/client/settings" replace />;
  }
  return <Profile />;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return user ? children : <Navigate to="/login" />;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return user ? <Navigate to="/dashboard" /> : children;
};

// Main Layout Component
const MainLayout = ({ children }) => {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  
  if (!user) return null;
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar onOpenSidebar={() => setSidebarOpen(true)} />
      <SubscriptionBanner />
      <div className="flex">
        {/* Desktop sidebar */}
        <div className="hidden md:block">
          <Sidebar />
        </div>
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-40 flex md:hidden">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/40" onClick={() => setSidebarOpen(false)} aria-hidden="true"></div>
            {/* Drawer */}
            <div className="relative ml-0 w-64 bg-white shadow-xl h-screen z-50">
              <Sidebar onClose={() => setSidebarOpen(false)} />
            </div>
          </div>
        )}
        <main className="flex-1 p-4 sm:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <FarmProvider>
          <Router>
          <div className="App">
            <Toaster 
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#22c55e',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
            
            <Routes>
              {/* Public Routes */}
              <Route 
                path="/" 
                element={<Landing />} 
              />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/academy" element={<Academy />} />
              <Route path="/academy/:slug" element={<AcademyCourse />} />
              <Route 
                path="/login" 
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                } 
              />
              <Route 
                path="/forgot-password" 
                element={
                  <PublicRoute>
                    <ForgotPassword />
                  </PublicRoute>
                } 
              />
              <Route 
                path="/reset-password" 
                element={
                  <PublicRoute>
                    <ResetPassword />
                  </PublicRoute>
                } 
              />
              <Route 
                path="/register" 
                element={
                  <PublicRoute>
                    <Register />
                  </PublicRoute>
                } 
              />
              <Route path="/oauth/callback" element={<OAuthCallback/>} />
              <Route path="/invite/accept" element={<AcceptInvite />} />
              
              {/* Protected Routes */}
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <DashboardRedirect />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/farm/setup" 
                element={
                  <ProtectedRoute>
                    <FarmerOnlyRoute>
                      <MainLayout>
                        <FarmSetup />
                      </MainLayout>
                    </FarmerOnlyRoute>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/farm/:farmId" 
                element={
                  <ProtectedRoute>
                    <FarmerOnlyRoute>
                      <MainLayout>
                        <FarmManagement />
                      </MainLayout>
                    </FarmerOnlyRoute>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/farm/:farmId/settings" 
                element={
                  <ProtectedRoute>
                    <FarmerOnlyRoute>
                      <MainLayout>
                        <FarmSettings />
                      </MainLayout>
                    </FarmerOnlyRoute>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/farm/:farmId/flocks" 
                element={
                  <ProtectedRoute>
                    <FarmerOnlyRoute>
                      <MainLayout>
                        <FlockList />
                      </MainLayout>
                    </FarmerOnlyRoute>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/farms" 
                element={
                  <ProtectedRoute>
                    <FarmerOnlyRoute>
                      <MainLayout>
                        <Farms />
                      </MainLayout>
                    </FarmerOnlyRoute>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/activities" 
                element={
                  <ProtectedRoute>
                    <FarmerOnlyRoute>
                      <MainLayout>
                        <Activities />
                      </MainLayout>
                    </FarmerOnlyRoute>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/hatching" 
                element={
                  <ProtectedRoute>
                    <FarmerOnlyRoute>
                      <MainLayout>
                        <Hatching />
                      </MainLayout>
                    </FarmerOnlyRoute>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/hatching/:batchId" 
                element={
                  <ProtectedRoute>
                    <FarmerOnlyRoute>
                      <MainLayout>
                        <BatchDetail />
                      </MainLayout>
                    </FarmerOnlyRoute>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/records" 
                element={
                  <ProtectedRoute>
                    <FarmerOnlyRoute>
                      <MainLayout>
                        <Records />
                      </MainLayout>
                    </FarmerOnlyRoute>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/ai-assistant" 
                element={
                  <ProtectedRoute>
                    <FarmerOnlyRoute>
                      <MainLayout>
                        <ErrorBoundary>
                          <AIAssistant />
                        </ErrorBoundary>
                      </MainLayout>
                    </FarmerOnlyRoute>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ProfileRedirect />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/market" 
                element={
                  <ProtectedRoute>
                    <FarmerOnlyRoute>
                      <MainLayout>
                        <Market />
                      </MainLayout>
                    </FarmerOnlyRoute>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/billing" 
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Billing />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Admin />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/shop" 
                element={
                  <ProtectedRoute>
                    <FarmerOnlyRoute>
                      <MainLayout>
                        <Shop />
                      </MainLayout>
                    </FarmerOnlyRoute>
                  </ProtectedRoute>
                } 
              />
              
              {/* Public Shop Route (no auth needed) */}
              <Route path="/order/:slug" element={<PublicShop />} />
              <Route path="/shop/:slug" element={<PublicShop />} />
              
              {/* Shared Chat Route (public - no auth needed) */}
              <Route path="/chat/shared/:shareToken" element={<SharedChat />} />
              
              {/* Supplier Routes */}
              <Route 
                path="/supplier/dashboard" 
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <SupplierDashboard />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/supplier/products" 
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <SupplierProducts />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/supplier/products/new" 
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <SupplierProducts />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/supplier/orders" 
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <SupplierOrders />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/supplier/orders/:orderId" 
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <SupplierOrders />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/supplier/settings" 
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <SupplierSettings />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />
              
              {/* Client Routes */}
              <Route 
                path="/client/dashboard" 
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ClientDashboard />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/client/explore" 
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ClientExplore />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/client/orders" 
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ClientOrders />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/client/favorites" 
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ClientExplore />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/client/settings" 
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <ClientSettings />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />

              {/* Wallet - Available to all account types */}
              <Route 
                path="/wallet" 
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Wallet />
                    </MainLayout>
                  </ProtectedRoute>
                } 
              />
              
              {/* Default redirect for unknown routes */}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
            <FeedbackButton />
          </div>
        </Router>
      </FarmProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
